<template>
  <!-- horizon top nav (FI-1869): edge-attached translucent steelblue bar per
       the v27 unified-toolbar treatment. nav toggle + brand on the left; sos
       alarm, notifications bell (desktop), help menu, and the account avatar
       on the right (classic parity: navbar.vue keeps help + avatar in the
       bar). section navigation lives entirely in the rail tree. class names
       deliberately avoid .nav-link/.nav-tabs/.bg-info (navbar.vue's unscoped
       styles restyle those app-wide even unmounted), but the popovers reuse
       navbar's global .nc-msg-* / .profile-* styles on purpose so both
       surfaces stay pixel-identical. -->
  <nav class="fi-tn" aria-label="Primary">
    <div class="fi-tn-bar">
      <!-- mobile only: opens the drawer. on desktop the collapse toggle
           lives at the foot of the rail in both states (chaz round 9) -->
      <button
        v-if="isMobile"
        v-b-tooltip.hover.bottom="barTip(navState.mobileOpen ? 'Close navigation' : 'Open navigation')"
        class="fi-tn-toggle"
        :aria-label="navState.mobileOpen ? 'Close navigation' : 'Open navigation'"
        :aria-expanded="navState.mobileOpen ? 'true' : 'false'"
        aria-controls="fi-sn-drawer"
        @click="onToggleNav"
      >
        <!-- standard panel-left glyph: left column filled while the drawer
             is open, outline otherwise (fa 4.7 has no sidebar icon) -->
        <svg class="fi-tn-toggle-glyph" viewBox="0 0 20 16" aria-hidden="true">
          <rect x="1" y="1" width="18" height="14" rx="3" fill="none" stroke="currentColor" stroke-width="1.6"/>
          <line x1="7.5" y1="1.8" x2="7.5" y2="14.2" stroke="currentColor" stroke-width="1.6"/>
          <rect v-if="navState.mobileOpen" x="2" y="2" width="4.6" height="12" rx="1.6" fill="currentColor" stroke="none"/>
        </svg>
      </button>
      <!-- on phone widths the lockup centers in the bar (ios-style title):
           position says "identity, not action", which the left-aligned bare
           glyph next to the drawer toggle failed to. while the sos pill is
           up the brand yields the center entirely -- with the fa button in
           the cluster the pill reaches past the bar's midpoint on 375px,
           and an open emergency outranks identity anyway -->
      <div class="fi-tn-brand" :class="{ 'fi-tn-brand--yield': sosItem && sosAlarmActive }">
        <!-- the canonical cloud-with-arrows mark (LogoMark.svg / www logo.svg)
             inlined verbatim with currentColor so it renders white here -->
        <svg class="fi-tn-brand-logo" viewBox="-2 0 200 128" fill="none" aria-hidden="true">
          <g stroke="currentColor" stroke-width="8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M36 120 C18 120 4 106 4 88 C4 74 14 62 28 57 C26 42 36 30 52 30 C58 30 64 32 68 36 C76 18 92 8 110 8 C138 8 160 30 162 57 C180 60 192 76 192 92 C192 108 178 120 162 120 Z"/>
            <path d="M65 64 H133 M116 48 L133 64 L116 80"/>
            <path d="M133 92 H65 M82 76 L65 92 L82 108"/>
          </g>
        </svg>
        <span class="fi-tn-brand-name">Fleet Integrations</span>
      </div>
      <div class="fi-tn-spacer"></div>
      <!-- the red pill is an alarm, not a nav link: it appears only while an
           sos request is actually open (everyday access to the sos page lives
           in the rail under fleet) -->
      <router-link
        v-if="sosItem && sosAlarmActive"
        :to="sosItem.to"
        class="fi-tn-sos"
        title="An SOS request is open"
      >
        <i class="fa fa-exclamation-triangle" aria-hidden="true"></i><span class="fi-tn-sos-label">SOS</span>
      </router-link>

      <!-- mobile only: fleet assistant rides the bar instead of floating
           over content (the launcher hides its pill under 992 when the
           horizon shell sets bar-launches). glyph is the user's chosen FA
           avatar; it plays its attention animation while a reply finished
           unread behind a closed panel -->
      <button
        v-if="isMobile && faAvailable"
        v-b-tooltip.hover.bottom="barTip('Fleet Assistant')"
        class="fi-tn-fa"
        aria-label="Fleet Assistant"
        @click.stop="openFa"
      >
        <fa_avatar class="fi-tn-fa-glyph" :variant="avatarVariant" :active="faUnread" />
      </button>

      <!-- desktop only: on mobile the notifications live in the account
           panel (classic desktop dropdown parity), so the bell hides -->
      <div class="fi-tn-bell-wrap">
        <button
          v-b-tooltip.hover.bottom="barTip('Notifications')"
          class="fi-tn-bell"
          aria-label="Notifications"
          aria-haspopup="true"
          :aria-expanded="notificationsOpen ? 'true' : 'false'"
          @click.stop="toggleNotifications"
        >
          <i class="fa fa-bell-o" aria-hidden="true"></i>
          <span v-if="unreadCount > 0" class="profile-avatar-badge">{{ unreadBadgeText }}</span>
        </button>

        <!-- notifications popover: the FI-1432 iOS-Messages card list, so
             opening it never navigates the user away from their page -->
        <div v-if="notificationsOpen" class="fi-tn-nc-pop" @click.stop>
          <FiNotificationList @closed="closeAll" />
        </div>
      </div>

      <!-- help menu: the classic bar linked straight to the help center; the
           horizon icon opens the user's help options plus the legal pages.
           desktop only (chaz round 12: the phone bar was crowding); phones
           reach help center / legal / the assistant through the rail's help
           section instead (round 13) -->
      <div v-if="!isMobile" class="fi-tn-help-wrap">
        <button
          v-b-tooltip.hover.bottom="barTip('Help')"
          class="fi-tn-help"
          aria-label="Help"
          aria-haspopup="true"
          :aria-expanded="helpOpen ? 'true' : 'false'"
          @click.stop="toggleHelp"
        >
          <i class="fa fa-question-circle" aria-hidden="true"></i>
        </button>
        <div v-if="helpOpen" class="fi-tn-help-pop" @click.stop>
          <template v-for="(g, gi) in helpMenu">
            <div v-if="gi > 0" :key="g.key + '-div'" class="fi-tn-pop-divider"></div>
            <div v-if="g.label" :key="g.key + '-label'" class="fi-tn-menu-label">{{ g.label }}</div>
            <template v-for="mi in g.items">
              <a
                v-if="mi.href"
                :key="mi.key"
                :href="mi.href"
                target="_blank"
                rel="noopener"
                class="fi-tn-menu-item"
                @click="closeAll"
              >
                <i class="fa fi-tn-menu-icon" :class="mi.icon" aria-hidden="true"></i>
                <span>{{ mi.label }}</span>
                <i class="fa fa-external-link fi-tn-menu-ext" aria-hidden="true"></i>
              </a>
              <router-link
                v-else
                :key="mi.key"
                :to="mi.to"
                class="fi-tn-menu-item"
                @click.native="closeAll"
              >
                <i class="fa fi-tn-menu-icon" :class="mi.icon" aria-hidden="true"></i>
                <span>{{ mi.label }}</span>
              </router-link>
            </template>
          </template>
        </div>
      </div>

      <!-- account avatar: desktop opens the profile popover (notifications
           stay in the bell); mobile opens the combined account panel below,
           so the avatar carries the unread badge there instead. the circle
           itself is navbar's global .profile-avatar so it matches classic
           exactly (no outline) -->
      <div class="fi-tn-avatar-wrap">
        <button
          v-b-tooltip.hover.bottom="barTip(displayName)"
          class="fi-tn-avatar-btn"
          aria-label="Account"
          aria-haspopup="true"
          :aria-expanded="avatarOpen ? 'true' : 'false'"
          @click.stop="toggleAvatar"
        >
          <span class="profile-avatar">
            {{ initials }}
            <span v-if="isMobile && unreadCount > 0" class="profile-avatar-badge">{{ unreadBadgeText }}</span>
          </span>
        </button>

        <!-- desktop profile popover: markup + classes lifted from navbar's
             profile dropdown (identity card header, role tag, icon actions,
             theme toggle), styled by its global .profile-* rules -->
        <div v-if="avatarOpen && !isMobile" class="fi-tn-profile-pop" @click.stop>
          <div class="profile-dropdown-header">
            <span class="profile-dd-avatar" :title="displayName">{{ initials }}</span>
            <div class="profile-dd-identity">
              <div class="profile-name-row">
                <span class="profile-display-name">{{ displayName }}</span>
                <button
                  v-if="user.role === 'admin'"
                  type="button"
                  class="profile-role-tag profile-role-tag--interactive"
                  :class="viewAsNonAdmin ? 'profile-role-tag-muggle' : 'profile-role-tag-admin'"
                  :title="viewAsNonAdmin ? 'Switch back to the admin view' : 'Preview the app as a non-admin'"
                  @click.stop.prevent="toggleViewAs"
                >
                  <i class="fa" :class="viewAsNonAdmin ? 'fa-magic fa-flip-horizontal' : 'fa-shield'" aria-hidden="true"></i>
                  <span>{{ viewAsNonAdmin ? 'MUGGLE' : 'ADMIN' }}</span>
                </button>
                <span
                  v-else
                  class="profile-role-tag"
                  :class="'profile-role-tag-' + (user.role || 'other')"
                >
                  <i class="fa" :class="roleIconFor(user.role)" aria-hidden="true"></i>
                  <span>{{ roleDisplayLabel(user.role) }}</span>
                </span>
              </div>
              <div class="profile-email">{{ user.username }}</div>
            </div>
          </div>
          <template v-if="ctx.isEffectiveAdmin">
            <div class="fi-tn-pop-divider"></div>
            <div class="profile-theme-row" @click.stop>
              <span class="profile-theme-label">Theme</span>
              <div class="profile-theme-toggle" role="group" aria-label="Theme">
                <button
                  type="button"
                  class="profile-theme-opt"
                  :class="{ 'profile-theme-opt--active': themeChoice === 'original' }"
                  :aria-pressed="String(themeChoice === 'original')"
                  @click.stop.prevent="setSiteTheme('original')"
                >
                  <span class="profile-theme-swatches" aria-hidden="true">
                    <span class="profile-theme-swatch" style="background:#4682b4"></span>
                    <span class="profile-theme-swatch" style="background:#007bff"></span>
                    <span class="profile-theme-swatch" style="background:#b58100"></span>
                  </span>
                  <span>Classic</span>
                </button>
                <button
                  type="button"
                  class="profile-theme-opt"
                  :class="{ 'profile-theme-opt--active': themeChoice === 'new' }"
                  :aria-pressed="String(themeChoice === 'new')"
                  @click.stop.prevent="setSiteTheme('new')"
                >
                  <span class="profile-theme-swatches" aria-hidden="true">
                    <span class="profile-theme-swatch" style="background:#15324d"></span>
                    <span class="profile-theme-swatch" style="background:#007ac7"></span>
                    <span class="profile-theme-swatch" style="background:#7b3fb0"></span>
                  </span>
                  <span>Horizon</span>
                </button>
                <button
                  type="button"
                  class="profile-theme-opt"
                  :class="{ 'profile-theme-opt--active': themeChoice === 'midnight' }"
                  :aria-pressed="String(themeChoice === 'midnight')"
                  @click.stop.prevent="setSiteTheme('midnight')"
                >
                  <span class="profile-theme-swatches" aria-hidden="true">
                    <span class="profile-theme-swatch" style="background:#0e1b2b"></span>
                    <span class="profile-theme-swatch" style="background:#6fb1e8"></span>
                    <span class="profile-theme-swatch" style="background:#7b3fb0"></span>
                  </span>
                  <span>Midnight</span>
                </button>
              </div>
            </div>
          </template>
          <!-- account actions as list rows under the theme picker (chaz
               round 7: replaces the icon chips in the header's top-right;
               the mobile panel keeps the classic header chips) -->
          <div class="fi-tn-pop-divider"></div>
          <router-link
            :to="{ name: 'user_detail', query: { username: user.username } }"
            class="fi-tn-menu-item"
            @click.native="closeAll"
          >
            <i class="fa fa-user-o fi-tn-menu-icon" aria-hidden="true"></i>
            <span>Manage Profile</span>
          </router-link>
          <button type="button" class="fi-tn-menu-item fi-tn-menu-item--danger" @click="logout">
            <i class="fa fa-sign-out fi-tn-menu-icon" aria-hidden="true"></i>
            <span>Logout</span>
          </button>
        </div>
      </div>
    </div>

    <!-- mobile account panel: the classic desktop dropdown (identity card +
         theme + notifications) rendered as a full page-sized sheet below the
         bar, per the horizon mobile design -->
    <div v-if="avatarOpen && isMobile" class="fi-tn-account-panel" @click.stop>
      <div class="profile-dropdown-header">
        <span class="profile-dd-avatar" :title="displayName">{{ initials }}</span>
        <div class="profile-dd-identity">
          <div class="profile-name-row">
            <span class="profile-display-name">{{ displayName }}</span>
            <button
              v-if="user.role === 'admin'"
              type="button"
              class="profile-role-tag profile-role-tag--interactive"
              :class="viewAsNonAdmin ? 'profile-role-tag-muggle' : 'profile-role-tag-admin'"
              :title="viewAsNonAdmin ? 'Switch back to the admin view' : 'Preview the app as a non-admin'"
              @click.stop.prevent="toggleViewAs"
            >
              <i class="fa" :class="viewAsNonAdmin ? 'fa-magic fa-flip-horizontal' : 'fa-shield'" aria-hidden="true"></i>
              <span>{{ viewAsNonAdmin ? 'MUGGLE' : 'ADMIN' }}</span>
            </button>
            <span
              v-else
              class="profile-role-tag"
              :class="'profile-role-tag-' + (user.role || 'other')"
            >
              <i class="fa" :class="roleIconFor(user.role)" aria-hidden="true"></i>
              <span>{{ roleDisplayLabel(user.role) }}</span>
            </span>
          </div>
          <div class="profile-email">{{ user.username }}</div>
        </div>
        <div class="profile-dd-actions">
          <b-button
            size="sm"
            variant="outline-secondary"
            :to="{ name: 'user_detail', query: { username: user.username } }"
            class="profile-dd-btn"
            title="Manage Profile"
            aria-label="Manage Profile"
            @click.native="closeAll"
          >
            <i class="fa fa-user-o" aria-hidden="true"></i>
          </b-button>
          <b-button
            size="sm"
            variant="outline-danger"
            class="profile-dd-btn"
            title="Logout"
            aria-label="Logout"
            @click="logout"
          >
            <i class="fa fa-sign-out" aria-hidden="true"></i>
          </b-button>
        </div>
      </div>
      <template v-if="ctx.isEffectiveAdmin">
        <div class="fi-tn-pop-divider"></div>
        <div class="profile-theme-row" @click.stop>
          <span class="profile-theme-label">Theme</span>
          <div class="profile-theme-toggle" role="group" aria-label="Theme">
            <button
              type="button"
              class="profile-theme-opt"
              :class="{ 'profile-theme-opt--active': themeChoice === 'original' }"
              :aria-pressed="String(themeChoice === 'original')"
              @click.stop.prevent="setSiteTheme('original')"
            >
              <span class="profile-theme-swatches" aria-hidden="true">
                <span class="profile-theme-swatch" style="background:#4682b4"></span>
                <span class="profile-theme-swatch" style="background:#007bff"></span>
                <span class="profile-theme-swatch" style="background:#b58100"></span>
              </span>
              <span>Classic</span>
            </button>
            <button
              type="button"
              class="profile-theme-opt"
              :class="{ 'profile-theme-opt--active': themeChoice === 'new' }"
              :aria-pressed="String(themeChoice === 'new')"
              @click.stop.prevent="setSiteTheme('new')"
            >
              <span class="profile-theme-swatches" aria-hidden="true">
                <span class="profile-theme-swatch" style="background:#15324d"></span>
                <span class="profile-theme-swatch" style="background:#007ac7"></span>
                <span class="profile-theme-swatch" style="background:#7b3fb0"></span>
              </span>
              <span>Horizon</span>
            </button>
            <button
              type="button"
              class="profile-theme-opt"
              :class="{ 'profile-theme-opt--active': themeChoice === 'midnight' }"
              :aria-pressed="String(themeChoice === 'midnight')"
              @click.stop.prevent="setSiteTheme('midnight')"
            >
              <span class="profile-theme-swatches" aria-hidden="true">
                <span class="profile-theme-swatch" style="background:#0e1b2b"></span>
                <span class="profile-theme-swatch" style="background:#6fb1e8"></span>
                <span class="profile-theme-swatch" style="background:#7b3fb0"></span>
              </span>
              <span>Midnight</span>
            </button>
          </div>
        </div>
      </template>
      <div class="fi-tn-pop-divider"></div>
      <FiNotificationList @closed="closeAll" />
    </div>
  </nav>
</template>

<script>
import { mapState } from 'vuex'
import { get_sos_history, update_site_theme } from '@/networking/apis'
import { buildNavContext, resolveTopBarItems, resolveHelpMenu, faLauncherAvailable } from '@/nav/registry'
import { navState, toggleCollapsed, setMobileOpen, noHoverDevice } from '@/nav/nav_state'
import { roleIconForInternal, roleLabelForInternal } from '@/helpers/role_display'
import FiNotificationList from './fi_notification_list.vue'
import fa_avatar from '../fa_avatar.vue'

// the horizon rail hides itself at max-width:991px (fi_section_nav.vue @media;
// App.vue drops the content margin at the same width). the bar's drawer toggle
// has to flip on the SAME query. it keyed off window.innerWidth, which counts
// the scrollbar -- so with always-on scrollbars a ~15px band (~992-1006px
// innerWidth) left the rail already hidden while innerWidth still read >= 992,
// so no toggle rendered and nav was unreachable. matchMedia evaluates the exact
// css query, so the toggle and the rail cross the breakpoint together.
const RAIL_HIDDEN_QUERY = '(max-width: 991px)'
function railHiddenNow() {
  return !!(window.matchMedia && window.matchMedia(RAIL_HIDDEN_QUERY).matches)
}

export default {
  name: 'fi_top_nav',
  components: { FiNotificationList, fa_avatar },
  data() {
    return {
      navState,
      notificationsOpen: false,
      helpOpen: false,
      avatarOpen: false,
      isMobile: railHiddenNow(),
      sosAlarmActive: false,
      // mirrors the launcher's hasUnread via the fa-unread-change event
      faUnread: false,
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
      viewAs: state => state.profile.view_as,
      siteTheme: state => state.profile.site_theme,
      unreadCount: state => state.notifications.unreadCount,
    }),
    ctx() {
      return buildNavContext(this.user, this.viewAs)
    },
    sosItem() {
      return resolveTopBarItems(this.ctx).find(i => i.key === 'sos_alarm') || null
    },
    helpMenu() {
      return resolveHelpMenu(this.ctx)
    },
    faAvailable() {
      // same gate as the floating pill in App.vue
      return faLauncherAvailable(this.user, this.$route.name)
    },
    // which avatar the assistant wears (same fallback chain as the launcher)
    avatarVariant() {
      const p = this.user && this.user.preferences
      const v = p && p.fa_icon
      return (v === 'cloud' || v === 'stapler' || v === 'nova' || v === 'plasma') ? v : 'bubble'
    },
    unreadBadgeText() {
      return this.unreadCount > 9 ? '9+' : String(this.unreadCount)
    },
    viewAsNonAdmin() {
      return this.viewAs === 'non-admin'
    },
    // picker state: unknown stored values render as classic, matching the
    // applyFiTheme coercion in the profile store
    themeChoice() {
      return (this.siteTheme === 'new' || this.siteTheme === 'midnight') ? this.siteTheme : 'original'
    },
    initials() {
      // mirrors navbar userInitials (navbar.vue:734-745): first + last
      // initials from full_name, first letter of the local part for emails
      const name = (this.user && (this.user.full_name || this.user.username)) || ''
      if (name.includes('@')) {
        return name.split('@')[0].charAt(0).toUpperCase()
      }
      const parts = name.trim().split(/\s+/)
      if (parts.length >= 2) {
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
      }
      return name.charAt(0).toUpperCase() || '?'
    },
    displayName() {
      const u = this.user || {}
      return u.full_name || u.username
    },
  },
  watch: {
    $route() {
      this.closeAll()
    },
    'user.tenant'() {
      this.sosAlarmActive = false
      this.refreshSosAlarm()
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocClick)
    document.addEventListener('keydown', this.onKeydown)
    // track the rail-hidden breakpoint off the same media query the css uses.
    // matchMedia fires on scrollbar-induced flips too, which 'resize' misses
    // (content growing tall enough to add a scrollbar can cross the boundary
    // with no window resize).
    this._railMq = window.matchMedia(RAIL_HIDDEN_QUERY)
    this._onRailMq = (e) => { this.isMobile = e.matches }
    if (this._railMq.addEventListener) this._railMq.addEventListener('change', this._onRailMq)
    else this._railMq.addListener(this._onRailMq) // safari < 14
    this._faUnreadHandler = (v) => { this.faUnread = !!v }
    this.$root.$on('fa-unread-change', this._faUnreadHandler)
    // no active-sos store exists, so the alarm pill polls history itself on
    // the same 60s cadence as the notification poll in App.vue
    this._sosTimer = setInterval(this.refreshSosAlarm, 60000)
    this.refreshSosAlarm()
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocClick)
    document.removeEventListener('keydown', this.onKeydown)
    if (this._railMq) {
      if (this._railMq.removeEventListener) this._railMq.removeEventListener('change', this._onRailMq)
      else this._railMq.removeListener(this._onRailMq)
    }
    if (this._sosTimer) clearInterval(this._sosTimer)
    if (this._faUnreadHandler) this.$root.$off('fa-unread-change', this._faUnreadHandler)
  },
  methods: {
    onToggleNav() {
      // one button, viewport-dependent: drawer on small screens, icon-rail
      // collapse on desktop (mirrors the rail's own breakpoint at 991px)
      if (railHiddenNow()) {
        setMobileOpen(!navState.mobileOpen)
      } else {
        // a drawer left open on a narrow window must not linger as state
        // once the viewport is desktop again
        setMobileOpen(false)
        toggleCollapsed()
      }
    },
    toggleNotifications() {
      this.helpOpen = false
      this.avatarOpen = false
      this.notificationsOpen = !this.notificationsOpen
      if (this.notificationsOpen) {
        // same freshness contract as the navbar dropdown: App.vue owns the
        // 60s poll, this asks it for an immediate refresh on open
        try { this.$root.$emit('nc-refresh') } catch (_) { /* bus not ready */ }
      }
    },
    toggleHelp() {
      this.notificationsOpen = false
      this.avatarOpen = false
      this.helpOpen = !this.helpOpen
    },
    openFa() {
      this.closeAll()
      setMobileOpen(false)
      // the launcher component owns the panel; it listens on $root
      this.$root.$emit('open-fleet-assistant')
    },
    toggleAvatar() {
      this.notificationsOpen = false
      this.helpOpen = false
      this.avatarOpen = !this.avatarOpen
      if (this.avatarOpen && this.isMobile) {
        // the account panel shows notifications and sits under the drawer's
        // z-index, so the drawer must not stay open over it
        setMobileOpen(false)
        try { this.$root.$emit('nc-refresh') } catch (_) { /* bus not ready */ }
      }
    },
    closeAll() {
      this.notificationsOpen = false
      this.helpOpen = false
      this.avatarOpen = false
    },
    // hover tooltips for the bar icons, same treatment as the rail rows
    // (fi_section_nav rowTip); .fi-sn-tip is unscoped there so it applies.
    // suppressed on touch devices: a tap emulates hover and the tip sticks
    // open over the popover it just opened (chaz round 12)
    barTip(text) {
      if (noHoverDevice()) return { title: '' }
      return { title: text || '', delay: { show: 450, hide: 0 }, boundary: 'window', customClass: 'fi-sn-tip' }
    },
    async refreshSosAlarm() {
      if (!this.sosItem || !this.user || !this.user.tenant) {
        this.sosAlarmActive = false
        return
      }
      try {
        // yesterday..today keeps the window small while covering midnight;
        // "active" = a request still in sos_status 'open' (the only other
        // lifecycle state is 'cancelled', process_inbound_sos.py)
        const pad = n => String(n).padStart(2, '0')
        const fmt = d => d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
        const now = new Date()
        const resp = await get_sos_history(this.user.tenant, fmt(new Date(now.getTime() - 86400000)), fmt(now))
        const events = (resp && resp.value && resp.value.data && resp.value.data.events) || []
        this.sosAlarmActive = events.some(e => e && e.sos_status === 'open')
      } catch (_) {
        // best effort: an unreachable history endpoint must not fake an alarm
        this.sosAlarmActive = false
      }
    },
    onDocClick() {
      this.closeAll()
    },
    onKeydown(e) {
      if (e.key === 'Escape') this.closeAll()
    },
    // viewer sees their own role, so the internal registry variant applies
    // (staff read "SYS ADMIN"/shield), mirroring navbar.vue:525-530
    roleIconFor(role) {
      return roleIconForInternal(role)
    },
    roleDisplayLabel(role) {
      return roleLabelForInternal(role)
    },
    toggleViewAs() {
      const next = this.viewAsNonAdmin ? null : 'non-admin'
      this.$store.commit('profile/set_view_as', next)
    },
    async setSiteTheme(next) {
      if (next === this.siteTheme) return
      const prev = this.siteTheme
      this.$store.commit('profile/set_site_theme', next)
      try {
        await update_site_theme(next)
      } catch (e) {
        this.$store.commit('profile/set_site_theme', prev)
      }
    },
    logout() {
      this.closeAll()
      this.$router.push('/login')
      this.$emit('authenticated', false)
    },
  },
}
</script>

<style scoped>
.fi-tn {
  /* fixed, not sticky: #app's overflow-x fallback (App.vue) can break
     sticky, and the rail's wrapper gives sticky no room anyway */
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1030;
}
.fi-tn-toggle-glyph {
  display: block;
  width: 20px;
  height: 16px;
}
.fi-tn-bar {
  /* positioning context for the mobile centered brand lockup */
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 52px;
  padding: 0 12px 0 6px;
  /* solid classic steelblue (no backdrop blur: the token went opaque, so a
     filter would only cost compositing) */
  background: var(--fi-topbar-bg);
  border-bottom: 1px solid var(--fi-topbar-line);
}
.fi-tn-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: none;
  background: transparent;
  color: #fff;
  font-size: 17px;
  border-radius: var(--fi-radius-sm);
  cursor: pointer;
  transition: background-color var(--fi-motion-quick);
}
.fi-tn-toggle:hover { background: rgba(255, 255, 255, 0.16); }
.fi-tn-brand {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-left: 8px;
  white-space: nowrap;
}
/* sized against the right cluster (36px avatar, 28px help glyph): the mark
   reads ~avatar-height and the wordmark ~the bar's visual midweight */
.fi-tn-brand-logo {
  display: block;
  width: 36px;
  height: 23px;
  color: #fff;
}
.fi-tn-brand-name {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
}
.fi-tn-spacer { flex: 1 1 auto; }
.fi-tn-sos {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  margin-right: 4px;
  border-radius: var(--fi-radius-sm);
  background: var(--fi-danger);
  /* !important: App.vue globals force steelblue on bare anchors */
  color: #fff !important;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
}
.fi-tn-sos:hover { background: #9d1616; color: #fff !important; text-decoration: none; }
.fi-tn-bell-wrap,
.fi-tn-help-wrap,
.fi-tn-avatar-wrap { position: relative; }
/* classic icon treatment (navbar.vue .nav-icon-btn): bare 28px glyph, no
   hover pill -- the glyph just brightens */
.fi-tn-bell,
.fi-tn-help {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 4px;
  border: none;
  background: transparent;
  border-radius: var(--fi-radius-sm);
  color: rgba(255, 255, 255, 0.85);
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  transition: color 0.15s;
}
.fi-tn-bell:hover,
.fi-tn-help:hover { color: #fff; }
/* optical size correction: fa-bell-o ink spans ~1.02em vs fa-question-circle's
   ~0.88em circle, so at a shared 28px the bell reads taller. 24px lands both
   glyphs at the same visual height, diminutive to the 36px avatar. */
.fi-tn-bell { font-size: 24px; }
/* mobile fa button: same bare-glyph treatment as bell/help. the bubble and
   cloud avatar variants draw in currentColor (white here); the art variants
   carry their own colors, matching how they render everywhere else */
.fi-tn-fa {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 4px;
  border: none;
  background: transparent;
  border-radius: var(--fi-radius-sm);
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  transition: color 0.15s;
}
.fi-tn-fa:hover { color: #fff; }
.fi-tn-fa-glyph {
  display: block;
  width: 26px;
  height: 26px;
}
/* bare wrapper: the circle itself is navbar's global .profile-avatar */
.fi-tn-avatar-btn {
  display: inline-flex;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
}
/* classic hover (navbar.vue .nav-icon-btn .nav-link:hover .profile-avatar) */
.fi-tn-avatar-btn:hover .profile-avatar { background: rgba(255, 255, 255, 0.3); }
/* bootstrap's reboot draws a focus ring on plain :focus, which lingers after
   a mouse click; keep the ring for keyboard focus only */
.fi-tn-toggle:focus,
.fi-tn-sos:focus,
.fi-tn-bell:focus,
.fi-tn-fa:focus,
.fi-tn-help:focus,
.fi-tn-avatar-btn:focus,
.fi-tn-menu-item:focus {
  outline: none;
  box-shadow: none;
}
.fi-tn-toggle:focus-visible,
.fi-tn-sos:focus-visible,
.fi-tn-bell:focus-visible,
.fi-tn-fa:focus-visible,
.fi-tn-help:focus-visible,
.fi-tn-avatar-btn:focus-visible,
.fi-tn-menu-item:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.45);
}
.fi-tn-nc-pop {
  position: absolute;
  top: 46px;
  right: 0;
  width: 400px;
  max-width: calc(100vw - 20px);
  /* App.vue's .container-fluid centers text app-wide; cards read left-aligned
     (the navbar variant got this for free from bootstrap's .dropdown-menu) */
  text-align: left;
  max-height: calc(100vh - 70px);
  overflow-y: auto;
  padding: 4px 2px 8px;
  border-radius: var(--fi-radius-lg);
  /* strong tier, not clear: notification cards are dense text over whatever
     page is behind them -- readability beats the glass effect here */
  background: var(--fi-glass-bg-strong);
  -webkit-backdrop-filter: blur(var(--fi-glass-blur)) saturate(var(--fi-glass-saturate));
  backdrop-filter: blur(var(--fi-glass-blur)) saturate(var(--fi-glass-saturate));
  border: 1px solid var(--fi-glass-line);
  box-shadow: var(--fi-glass-shadow);
  z-index: 2002;
}
.fi-tn-nc-pop .nc-msg-footer { padding: 8px 10px 2px; }
.fi-tn-help-pop {
  position: absolute;
  top: 46px;
  right: 0;
  width: 240px;
  max-width: calc(100vw - 20px);
  text-align: left;
  padding: 6px 0;
  border-radius: var(--fi-radius-md);
  background: var(--fi-glass-bg-strong);
  -webkit-backdrop-filter: blur(var(--fi-glass-blur)) saturate(var(--fi-glass-saturate));
  backdrop-filter: blur(var(--fi-glass-blur)) saturate(var(--fi-glass-saturate));
  border: 1px solid var(--fi-glass-line);
  box-shadow: var(--fi-glass-shadow);
  z-index: 2002;
}
.fi-tn-profile-pop {
  position: absolute;
  top: 46px;
  right: 0;
  /* wide enough that a typical name + role tag share the top row like the
     legacy dropdown */
  width: 340px;
  max-width: calc(100vw - 20px);
  text-align: left;
  padding: 8px 0;
  border-radius: var(--fi-radius-md);
  background: var(--fi-glass-bg-strong);
  -webkit-backdrop-filter: blur(var(--fi-glass-blur)) saturate(var(--fi-glass-saturate));
  backdrop-filter: blur(var(--fi-glass-blur)) saturate(var(--fi-glass-saturate));
  border: 1px solid var(--fi-glass-line);
  box-shadow: var(--fi-glass-shadow);
  z-index: 2002;
}
.fi-tn-menu-label {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fi-chrome-eyebrow);
  padding: 6px 14px 3px;
}
.fi-tn-menu-item {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 14px;
  /* !important: App.vue globals force steelblue on bare anchors */
  color: var(--fi-chrome-ink) !important;
  font-size: 13.5px;
  text-decoration: none;
  transition: background-color var(--fi-motion-quick);
}
.fi-tn-menu-item:hover {
  background: var(--fi-chrome-hover-bg);
  color: var(--fi-chrome-ink-strong) !important;
  text-decoration: none;
}
.fi-tn-menu-icon {
  flex: 0 0 auto;
  width: 17px;
  text-align: center;
  font-size: 14px;
  color: var(--fi-chrome-muted);
}
.fi-tn-menu-ext {
  margin-left: auto;
  font-size: 10px;
  opacity: 0.6;
}
/* the profile/logout rows reuse the menu-item look; the button variant
   needs the native chrome stripped. no `font` shorthand here: it would
   out-specify the class's 13.5px font-size and render logout larger than
   its sibling link rows (reboot already inherits the family on buttons) */
button.fi-tn-menu-item {
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
}
.fi-tn-menu-item--danger,
.fi-tn-menu-item--danger .fi-tn-menu-icon { color: var(--fi-chrome-danger) !important; }
.fi-tn-menu-item--danger:hover { background: var(--fi-chrome-danger-hover-bg); }
/* same drawing as bootstrap's .dropdown-divider between menu groups */
.fi-tn-pop-divider {
  height: 0;
  margin: 8px 0;
  border-top: 1px solid var(--fi-chrome-divider);
}
/* the account panel is a page, not a popover: opaque, full width, scrolls */
.fi-tn-account-panel {
  position: fixed;
  top: 52px;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  background: var(--fi-chrome-sheet-bg);
  text-align: left;
  padding: 4px 0 24px;
}
/* midnight: the identity card + theme picker reuse navbar's global
   .profile-* rules, which are inked for a light popover. re-ink them for
   the dark chrome here (classic never runs under midnight, and these are
   scoped to this component's template, so nothing leaks to the classic
   dropdown). the role/muggle chips keep their own light fills and stay. */
:root[data-fi-theme="midnight"] .profile-dropdown-header { background: transparent; }
:root[data-fi-theme="midnight"] .profile-display-name { color: var(--fi-chrome-ink-strong); }
:root[data-fi-theme="midnight"] .profile-email { color: var(--fi-chrome-muted); }
:root[data-fi-theme="midnight"] .profile-dd-avatar {
  border-color: rgba(111, 177, 232, 0.45);
  background: rgba(111, 177, 232, 0.12);
  color: #9cc7ec;
  box-shadow: none;
}
/* the unread badge's cutout ring should match the bar behind it -- the
   classic steelblue fallback rings wrong on the midnight blue gradient
   (scoped to the horizon wraps so the classic navbar keeps its own) */
:root[data-fi-theme="midnight"] .fi-tn-bell-wrap .profile-avatar-badge,
:root[data-fi-theme="midnight"] .fi-tn-avatar-wrap .profile-avatar-badge {
  box-shadow: 0 0 0 2px #142e4a;
}
:root[data-fi-theme="midnight"] .profile-theme-label { color: var(--fi-chrome-muted); }
:root[data-fi-theme="midnight"] .profile-theme-toggle {
  border-color: rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.06);
}
:root[data-fi-theme="midnight"] .profile-theme-opt { color: var(--fi-chrome-muted); }
:root[data-fi-theme="midnight"] .profile-theme-opt:hover { color: var(--fi-chrome-ink-strong); }
:root[data-fi-theme="midnight"] .profile-theme-opt--active,
:root[data-fi-theme="midnight"] .profile-theme-opt--active:hover { color: #fff; }
:root[data-fi-theme="midnight"] .profile-theme-swatch {
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.25);
}
:root[data-fi-theme="midnight"] .profile-theme-opt--active .profile-theme-swatch {
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.65);
}
@media (max-width: 991px) {
  /* centered brand lockup (ios-style title): the bar is the positioning
     context; pointer-events off so the mark never reads as tappable next
     to the real buttons */
  .fi-tn-brand {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    margin-left: 0;
    pointer-events: none;
  }
  .fi-tn-brand-logo { width: 30px; height: 19px; }
  /* with help desktop-only the ~164px lockup clears the fa + avatar cluster
     down to ~360px wide, so the wordmark rides along on phones */
  .fi-tn-brand-name { font-size: 15px; }
  /* an active sos pill takes the center's room (class set in the template) */
  .fi-tn-brand--yield { display: none; }
  .fi-tn-bell-wrap { display: none; }
}
</style>

<style>
/* midnight: the notification cards inside the bell popover / account panel
   come from fi_notification_list, whose .nc-msg-* styles are navbar globals
   inked for a light surface. the list's elements don't carry this
   component's scope attribute, so this block is unscoped on purpose; every
   rule is fenced to the two horizon containers (and classic never runs
   under midnight anyway). the type-colored pucks keep their light pastel
   fills -- they're chips and still read on dark. */
[data-fi-theme="midnight"] .fi-tn-nc-pop .nc-msg-header,
[data-fi-theme="midnight"] .fi-tn-account-panel .nc-msg-header { color: var(--fi-chrome-muted); }
[data-fi-theme="midnight"] .fi-tn-nc-pop .nc-msg-card,
[data-fi-theme="midnight"] .fi-tn-account-panel .nc-msg-card { color: var(--fi-chrome-ink); }
[data-fi-theme="midnight"] .fi-tn-nc-pop .nc-msg-card:hover,
[data-fi-theme="midnight"] .fi-tn-nc-pop .nc-msg-card:focus,
[data-fi-theme="midnight"] .fi-tn-account-panel .nc-msg-card:hover,
[data-fi-theme="midnight"] .fi-tn-account-panel .nc-msg-card:focus {
  background: var(--fi-chrome-hover-bg);
  color: var(--fi-chrome-ink);
}
[data-fi-theme="midnight"] .fi-tn-nc-pop .nc-msg-heading,
[data-fi-theme="midnight"] .fi-tn-account-panel .nc-msg-heading { color: var(--fi-chrome-ink-strong); }
[data-fi-theme="midnight"] .fi-tn-nc-pop .nc-msg-preview,
[data-fi-theme="midnight"] .fi-tn-account-panel .nc-msg-preview { color: var(--fi-chrome-text); }
[data-fi-theme="midnight"] .fi-tn-nc-pop .nc-msg-time,
[data-fi-theme="midnight"] .fi-tn-nc-pop .nc-msg-empty,
[data-fi-theme="midnight"] .fi-tn-account-panel .nc-msg-time,
[data-fi-theme="midnight"] .fi-tn-account-panel .nc-msg-empty { color: var(--fi-chrome-muted); }
[data-fi-theme="midnight"] .fi-tn-nc-pop .nc-msg-empty .fa,
[data-fi-theme="midnight"] .fi-tn-account-panel .nc-msg-empty .fa { color: rgba(255, 255, 255, 0.25); }
[data-fi-theme="midnight"] .fi-tn-nc-pop .nc-msg-card-unread::before,
[data-fi-theme="midnight"] .fi-tn-account-panel .nc-msg-card-unread::before { background: var(--fi-chrome-accent); }
[data-fi-theme="midnight"] .fi-tn-nc-pop .nc-msg-mark-all,
[data-fi-theme="midnight"] .fi-tn-nc-pop .nc-msg-see-all,
[data-fi-theme="midnight"] .fi-tn-account-panel .nc-msg-mark-all,
[data-fi-theme="midnight"] .fi-tn-account-panel .nc-msg-see-all { color: var(--fi-chrome-accent); }
[data-fi-theme="midnight"] .fi-tn-nc-pop .nc-msg-see-all:hover,
[data-fi-theme="midnight"] .fi-tn-account-panel .nc-msg-see-all:hover { color: #8fc3ee; }
</style>
